import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "../assets/css/skSelect.css";

/**
 * Compact custom select — drop-in replacement for a native <select>.
 *
 * The menu renders in a portal so it is never clipped by a parent's
 * `overflow: hidden` (headers and the scrolling data grid both do this).
 *
 * Props:
 *   value      : any                          — currently selected value
 *   onChange   : (value) => void              — called with the raw value
 *   options    : [{ value, label }]
 *   placeholder?: string
 *   width?     : number | string              — trigger width (default 170)
 *   size?      : "sm" | "md"                  — 34px filter chip | 44px form field
 *   invalid?   : boolean                      — red border for form validation
 *   disabled?  : boolean
 *   ariaLabel? : string
 */
const SkSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  width = 170,
  size = "sm",
  invalid = false,
  disabled = false,
  ariaLabel,
  onOpen,
  searchable= false,
  searchPlaceholder = "Search",
  // Searchable only: keep whatever the user types, even when it matches no
  // option. Turns the control into a text box with suggestions — which is what
  // a report filter wants (the list is a shortcut, not a whitelist).
  allowFreeText = false,
  /* Opt-in multi-select.
   *
   * With `multiple`, `value` is an ARRAY and `onChange` is handed one back;
   * the menu draws a checkbox per row and stays open while boxes are ticked.
   * Everything else — the single-value path every form on the site uses — is
   * untouched, because a select that silently started returning arrays would
   * break each of those in a different way. */
  multiple = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  /* One list, whichever mode: single is just the array with nought or one
     entry in it, so the checks below do not have to fork. */
  const selectedValues = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value === "" || value === null || value === undefined
    ? []
    : [value];

  const isPicked = (v) => selectedValues.includes(v);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  // What the menu actually shows. Everything below (keyboard nav, commit)
  // works off THIS list, not `options` — otherwise typing a search term makes
  // the highlight and Enter land on a different option than the one on screen.
  const filteredOptions = searchable
  ? options.filter((option) => String(option.label || "").toLowerCase().includes(searchTerm.toLocaleLowerCase())) : options;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    // const r = el.getBoundingClientRect();
    // setRect({
    //   top: r.bottom + 6,
    //   left: r.left,
    //   width: r.width,
    // });
    const r = el.getBoundingClientRect();

    setRect({
      top: r.bottom + 4,
      left: r.left,
      width: r.width,
    });
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
    setSearchTerm("");
  }, []);

  const open = () => {
    if (disabled) return;
    onOpen?.();
    place();
    // Free-text fields open with the current value ready to edit; plain
    // selects open on the full list.
    setSearchTerm(allowFreeText && value != null ? String(value) : "");
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOpen(true);
  };

  const commit = (opt) => {
    if (multiple) {
      /* Ticking is not choosing: the menu stays open so several boxes can be
         set in one visit, and closes when the reader clicks away. */
      const next = isPicked(opt.value)
        ? selectedValues.filter((v) => v !== opt.value)
        : [...selectedValues, opt.value];
      onChange?.(next);
      return;
    }

    onChange?.(opt.value);
    close();
    triggerRef.current?.focus();
  };

  // While open: close on outside click, and keep the menu pinned to the
  // trigger through scrolls/resizes (portal is fixed-positioned).
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e) => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        close();
      }
    };

    const onScroll = () => {
      place();
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isOpen, close, place]);

  const onKeyDown = (e) => {
    if (disabled) return;

    if (!isOpen) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        open();
      }
      return;
    }

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        close();
        triggerRef.current?.focus();
        break;
      case "ArrowDown":
        e.preventDefault();
        if (filteredOptions.length)
          setActiveIndex((i) => (i + 1) % filteredOptions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (filteredOptions.length)
          setActiveIndex(
            (i) => (i - 1 + filteredOptions.length) % filteredOptions.length
          );
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(filteredOptions.length - 1);
        break;
      case "Enter":
        e.preventDefault();
        if (filteredOptions[activeIndex]) commit(filteredOptions[activeIndex]);
        break;
      case " ":
        // A space is a legitimate character while typing a search term.
        if (searchable) break;
        e.preventDefault();
        if (filteredOptions[activeIndex]) commit(filteredOptions[activeIndex]);
        break;
      case "Tab":
        close();
        break;
      default:
        break;
    }
  };
  return (
    <div className="sk-select" style={{ width }}>
      {searchable ? (
  <div
    ref={triggerRef}
    className={
      `sk-select__trigger sk-select__trigger--${size}` +
      (isOpen ? " is-open" : "") +
      (invalid ? " is-invalid" : "")
    }
    // Re-open on a click when the input already holds focus (after picking a
    // value, onFocus no longer fires — without this the field looks dead).
    onClick={() => {
      if (!isOpen) open();
    }}
  >
    <input
      type="text"
      // Closed: show the picked option's label — or, for a free-text field,
      // whatever was typed (it has no matching option to label it).
      value={
        isOpen
          ? searchTerm
          : selected?.label ?? (allowFreeText && value != null ? String(value) : "")
      }
      placeholder={isOpen ? searchPlaceholder : placeholder}
      disabled={disabled}
      className="sk-select__search-input"
      aria-label={ariaLabel}
      onFocus={() => {
        if (!isOpen) {
          open();
        }
      }}
      onChange={(e) => {
        const text = e.target.value;

        // open() resets the term, so it has to run BEFORE we set it.
        if (!isOpen) open();

        setSearchTerm(text);
        setActiveIndex(0);

        // Free-text: every keystroke is the value, so the filter applies as
        // you type and an unlisted value is still accepted.
        if (allowFreeText) onChange?.(text);
      }}
      onKeyDown={onKeyDown}
    />

    <i
      className="bi bi-chevron-down sk-select__caret"
      aria-hidden="true"
    />
  </div>
): (
  <button
    type="button"
    ref={triggerRef}
    className={
      `sk-select__trigger sk-select__trigger--${size}` +
      (isOpen ? " is-open" : "") +
      (invalid ? " is-invalid" : "")
    }
    onClick={() => (isOpen ? close() : open())}
    onKeyDown={onKeyDown}
    disabled={disabled}
    aria-haspopup="listbox"
    aria-expanded={isOpen}
    aria-label={ariaLabel}
  >
    <span
      className={`sk-select__value${
        (multiple ? selectedValues.length > 0 : selected) ? "" : " is-placeholder"
      }`}
    >
      {multiple
        ? selectedValues.length === 0
          ? placeholder
          : selectedValues.length === 1
          ? options.find((o) => o.value === selectedValues[0])?.label ??
            selectedValues[0]
          : `${selectedValues.length} selected`
        : selected
        ? selected.label
        : placeholder}
    </span>

    <i
      className="bi bi-chevron-down sk-select__caret"
      aria-hidden="true"
    />
  </button>
)}
      {isOpen &&
        rect &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="sk-select__menu"
            role="listbox"
            style={{ top: rect.top, left: rect.left, minWidth: rect.width }}
          >
            {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, i) => (
              <div
                key={opt.value}
                role="option"
                aria-selected={isPicked(opt.value)}
                className={
                  "sk-select__option" +
                  (i === activeIndex ? " is-active" : "") +
                  (isPicked(opt.value) ? " is-selected" : "")
                }
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => commit(opt)}
              >
                {multiple && (
                  <input
                    type="checkbox"
                    className="form-check-input sk-select__check"
                    checked={isPicked(opt.value)}
                    readOnly
                    tabIndex={-1}
                  />
                )}
                <span className="sk-select__option-label">{opt.label}</span>
                {!multiple && isPicked(opt.value) && (
                  <i className="bi bi-check2" aria-hidden="true" />
                )}
              </div>
            ))) : (
              <div className="sk-select__no-results">
                No records found
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default SkSelect;
